// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Drawer } from '@mui/material';
import { X, Books, Trash } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { XGButton } from 'xg-cl';
import LineItemForm from '../LineItemForm/LineItemForm';
import UnsavedChangesModal from '../../Trip/Itinerary/UnsavedChangesModal/UnsavedChangesModal';
import DeleteModal from '../../Trip/DeleteModal/DeleteModal';
import { getLineItemServiceType } from '../../../constants/lineItemServiceTypes';
import { copyLineItemToLibrary } from '../../../services/lineitem/lineitem';
import { SuccessToast, ErrorToast } from '../../../utils/alerts';
import * as PropTypes from 'prop-types';
import * as P from '@phosphor-icons/react';
import './LineItemEditSlideout.css';

/**
 * LineItemEditSlideout - Slideout wrapper for LineItem create/edit form
 *
 * @param {boolean} open - Whether the slideout is open
 * @param {Function} onClose - Close handler
 * @param {Object} lineItem - Line item being edited (null for new)
 * @param {Function} onSave - Save handler
 * @param {number} tripId - Trip ID
 * @param {number} tenantId - Tenant ID
 * @param {boolean} isLibrary - Whether this is a library item
 */
const LineItemEditSlideout = ({
  open,
  onClose,
  lineItem = null,
  onSave,
  onSaveToLibrary,
  onDelete,
  tripId,
  tenantId,
  isLibrary = false,
  onUnsavedChange = null,
  onUnsavedResolve = null,
  shouldAttemptClose = false,
  onAttemptCloseHandled = null
}) => {
  const { t } = useTranslation(['trip', 'common']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({});

  // Get service type info for header
  const serviceTypeCode =
    lineItem?.servicetypecode || lineItem?.servicetypecode;
  const serviceType = getLineItemServiceType(serviceTypeCode);
  const serviceTypeName = serviceType?.name || '';
  const serviceTypeIcon = serviceType?.iconref || '';
  const isEditing = !!lineItem?.lineitemid;

  const ServiceTypeIcon =
    serviceTypeIcon && P[serviceTypeIcon] ? P[serviceTypeIcon] : null;

  const [formSubmitHandler, setFormSubmitHandler] = useState(null);
  const contentRef = useRef(null);
  const paperRef = useRef(null);

  const scrollToTop = () => {
    const targets = [paperRef.current, contentRef.current].filter(Boolean);
    if (targets.length === 0) return;

    targets.forEach((el) => {
      try {
        el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      } catch (e) {
        el.scrollTop = 0;
        el.scrollLeft = 0;
      }
    });
  };

  useEffect(() => {
    if (!open) return;
    // Defer scroll reset to avoid being overridden by transition/focus.
    const rafId = requestAnimationFrame(() => {
      scrollToTop();
      setTimeout(() => scrollToTop(), 0);
      setTimeout(() => scrollToTop(), 50);
    });

    return () => {
      try {
        cancelAnimationFrame(rafId);
      } catch (e) {
        /* no-op */
      }
    };
  }, [open, lineItem?.lineitemid, lineItem?.servicetypecode]);

  useEffect(() => {
    if (!open) {
      setHasUnsavedChanges(false);
      setShowUnsavedModal(false);
    }
  }, [open]);

  useEffect(() => {
    if (shouldAttemptClose) {
      handleClose();
      if (typeof onAttemptCloseHandled === 'function') {
        try {
          onAttemptCloseHandled();
        } catch (e) {
          /* no-op */
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAttemptClose]);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
      return;
    }
    onClose();
  };

  const handleSave = async (formData) => {
    const slideoutPerfStart = performance.now();
    console.log('⏱️ [PERF-SLIDEOUT] Save button clicked, starting save flow');
    console.log('🎯 LineItemEditSlideout.handleSave called with:', formData);
    setIsSubmitting(true);
    try {
      console.log('📞 Calling onSave prop...');
      const onSaveStart = performance.now();
      await onSave(formData);
      console.log(
        `⏱️ [PERF-SLIDEOUT] onSave completed in ${(performance.now() - onSaveStart).toFixed(2)}ms`
      );
      console.log('✅ onSave completed, closing slideout');
      setHasUnsavedChanges(false);
      setShowUnsavedModal(false);
      onClose();
      if (typeof onUnsavedResolve === 'function') {
        try {
          onUnsavedResolve('save');
        } catch (e) {
          /* no-op */
        }
      }
      const totalSlideoutTime = performance.now() - slideoutPerfStart;
      console.log(
        `⏱️ [PERF-SLIDEOUT] ✅ Complete save flow finished: ${totalSlideoutTime.toFixed(2)}ms (${(totalSlideoutTime / 1000).toFixed(2)}s)`
      );
    } catch (error) {
      const totalSlideoutTime = performance.now() - slideoutPerfStart;
      console.error(
        `⏱️ [PERF-SLIDEOUT] ❌ Save flow failed after ${totalSlideoutTime.toFixed(2)}ms:`,
        error
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveToLibrary = async () => {
    setIsSubmitting(true);

    // Auto-prefix vendorlink with https:// if it doesn't have a protocol
    const normalizedVendorLink = formData.vendorlink
      ? (() => {
          const trimmedLink = formData.vendorlink.trim();
          if (!trimmedLink) return null;
          // Check if it already has a protocol (http://, https://, ftp://, etc.)
          if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmedLink)) {
            return trimmedLink;
          }
          // Auto-prefix with https://
          return `https://${trimmedLink}`;
        })()
      : null;

    const payload = {
      servicetypecode: formData.servicetypecode,
      title: formData.title,
      description: formData.description || null,
      vendorname: formData.vendorname || null,
      vendorphone: formData.vendorphone || null,
      vendorlink: normalizedVendorLink,
      contactname: formData.contactname || null,
      startlocation: formData.startlocation || null,
      endlocation: formData.endlocation || null,
      starttime: formData.starttime || null,
      endtime: formData.endtime || null,
      durationtime: formData.durationtime || null,
      confirmationnumber: formData.confirmationnumber || null,
      serviceclass: formData.serviceclass || null,
      servicenumber: formData.servicenumber || null,
      enddayoffset:
        formData.enddayoffset === null || formData.enddayoffset === undefined
          ? null
          : parseInt(formData.enddayoffset, 10),
      mealsincluded:
        Array.isArray(formData.mealsincluded) &&
        formData.mealsincluded.length > 0
          ? formData.mealsincluded.join(', ')
          : null,
      internalnote: formData.internalnote || null,
      images: formData.images || [],
      attachments: formData.attachments || []
    };

    return await onSaveToLibrary(payload);
  };

  const handleDeleteClick = () => {
    if (!lineItem?.lineitemid) return;
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!lineItem?.lineitemid || typeof onDelete !== 'function') return;

    setIsDeleting(true);
    try {
      await onDelete(lineItem.lineitemid);
      setHasUnsavedChanges(false);
      setShowUnsavedModal(false);
      setIsDeleteModalOpen(false);
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFooterSave = () => {
    if (formSubmitHandler) {
      formSubmitHandler();
    }
  };

  const handleUnsavedModalAction = (action) => {
    switch (action) {
      case 'continue':
        setShowUnsavedModal(false);
        if (typeof onUnsavedResolve === 'function') {
          try {
            onUnsavedResolve('continue');
          } catch (e) {
            /* no-op */
          }
        }
        break;
      case 'discard':
        setHasUnsavedChanges(false);
        setShowUnsavedModal(false);
        onClose();
        if (typeof onUnsavedResolve === 'function') {
          try {
            onUnsavedResolve('discard');
          } catch (e) {
            /* no-op */
          }
        }
        break;
      case 'save':
        handleFooterSave();
        break;
      default:
        setShowUnsavedModal(false);
        if (typeof onUnsavedResolve === 'function') {
          try {
            onUnsavedResolve('continue');
          } catch (e) {
            /* no-op */
          }
        }
        break;
    }
  };

  const handleAddToLibrary = async () => {
    setIsSubmitting(true);

    try {
      if (!lineItem?.lineitemid) {
        await handleSaveToLibrary();
      } else {
        //Create from existing line item
        await copyLineItemToLibrary(lineItem.lineitemid);
        SuccessToast(t('library:serviceAddedToLibrary'));
      }
    } catch (error) {
      console.error('Error copying line item to library:', error);
      ErrorToast(t('errors:dayContentCouldNotBeAddedToLibrary'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Drawer
        hideBackdrop={!hasUnsavedChanges}
        open={open}
        onClose={handleClose}
        className={`line-item-edit-slideout__drawer ${isLibrary ? 'line-item-edit-slideout__drawer--library' : 'line-item-edit-slideout__drawer--trip'} ${open ? 'drawer-open' : ''}`}
        anchor='right'
        id='line-item-edit-slideout'
        PaperProps={{
          ref: paperRef
        }}
        SlideProps={{
          onEntered: () => {
            // Ensure we are at the top after the slide transition completes.
            scrollToTop();
            setTimeout(() => scrollToTop(), 0);
            setTimeout(() => scrollToTop(), 50);
          }
        }}
        ModalProps={{
          BackdropProps: {
            invisible: true
          },
          disableEnforceFocus: true,
          disableAutoFocus: true,
          disableRestoreFocus: true
        }}
      >
        <Box className='line-item-edit-slideout'>
          <Box className='line-item-edit-slideout__header'>
            <Box className='line-item-edit-slideout__header-title'>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {ServiceTypeIcon && (
                  <Box className='line-item-edit-slideout__icon'>
                    <ServiceTypeIcon size={23.33} />
                  </Box>
                )}
                <Typography
                  variant='h6'
                  className='line-item-edit-slideout__title'
                >
                  {`${isEditing ? t('common:edit') : t('common:add')} ${serviceTypeName}`}
                </Typography>
              </Box>
              <XGButton
                id='line-item-edit-slideout__close-btn'
                className='line-item-edit-slideout__close-btn'
                type='tertiary'
                size='small'
                onClick={handleClose}
                startIcon={<X size={21} />}
                variant='text'
                disabled={isSubmitting}
              />
            </Box>
          </Box>

          <Box className='line-item-edit-slideout__content' ref={contentRef}>
            <LineItemForm
              lineItem={lineItem}
              onSave={handleSave}
              formData={formData}
              setFormData={setFormData}
              onCancel={handleClose}
              isSubmitting={isSubmitting}
              tripId={tripId}
              tenantId={tenantId}
              isLibrary={isLibrary}
              hideActions={true}
              setSubmitHandler={setFormSubmitHandler}
              onUnsavedChange={(nextHasChanges) => {
                setHasUnsavedChanges(nextHasChanges);
                if (typeof onUnsavedChange === 'function') {
                  try {
                    onUnsavedChange(nextHasChanges);
                  } catch (e) {
                    /* no-op */
                  }
                }
              }}
            />
          </Box>

          <Box className='line-item-edit-slideout__footer'>
            <Box className='line-item-edit-slideout__footer-actions'>
              <Box>
                {!isLibrary && (
                  <XGButton
                    id='line-item-edit-slideout__add-to-library-btn'
                    type='secondary'
                    variant='outlined'
                    text={t('library:saveToLibrary')}
                    startIcon={<Books size={20} />}
                    onClick={handleAddToLibrary}
                    disabled={isSubmitting}
                  />
                )}

                {lineItem?.lineitemid &&
                  isLibrary &&
                  typeof onDelete === 'function' && (
                    <XGButton
                      id='line-item-edit-slideout__delete-btn'
                      type='error'
                      variant='outlined'
                      text={t('common:delete')}
                      startIcon={<Trash size={20} />}
                      onClick={handleDeleteClick}
                      disabled={isSubmitting || isDeleting}
                    />
                  )}
              </Box>
              <Box className='line-item-edit-slideout__primary-actions'>
                <XGButton
                  id='line-item-edit-slideout__cancel-btn'
                  type='secondary'
                  variant='outlined'
                  text={t('common:cancel')}
                  onClick={handleClose}
                  disabled={isSubmitting}
                />
                <XGButton
                  id='line-item-edit-slideout__save-btn'
                  type='primary'
                  variant='contained'
                  text={t('common:saveChanges')}
                  onClick={handleFooterSave}
                  disabled={isSubmitting || !hasUnsavedChanges}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onAction={handleUnsavedModalAction}
        isSubmitting={isSubmitting}
      />

      <DeleteModal
        id={`delete-line-item-slideout-modal-${lineItem?.lineitemid || 'new'}`}
        isOpenModal={isDeleteModalOpen}
        title={t('common:delete')}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteModalOpen(false)}
        description={t('common:thisActionCannotBeUndone')}
        isSubmitting={isDeleting}
      />
    </>
  );
};

LineItemEditSlideout.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  lineItem: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onSaveToLibrary: PropTypes.func,
  onDelete: PropTypes.func,
  tripId: PropTypes.number,
  tenantId: PropTypes.number,
  isLibrary: PropTypes.bool,
  onUnsavedChange: PropTypes.func,
  onUnsavedResolve: PropTypes.func,
  shouldAttemptClose: PropTypes.bool,
  onAttemptCloseHandled: PropTypes.func
};

export default LineItemEditSlideout;
